import * as React from "react";
import CustomerFilterRow from "./CustomerFilterRow";
import UserFilterRow from "./UserFilterRow";
import styled from "styled-components";
import { useEffect, useState } from "react";
import FilterEntry from "./FilterEntry";
import AddUserFilterModal from "./AddUserFilterModal";
import AddCustomerFilterModal from "./AddCustomerModal";
import ModalButton from "../common/ModalButton";
import classnames from "classnames";
import GlobalScope from "../../GlobalScope";
import {
  getActiveSwitcher,
  getNavigator,
  getSelectedTimeRange,
  refreshDayPilot,
  setClientStateFilterJson,
} from "../../bridge/daypilot";
import { updateSessionVariables } from "../../bridge/planner";

export type CustomerData = {
  id: number;
  vismaId: string;
  name: string;
  projects: ProjectData[];
};

export type ProjectData = {
  id: number;
  name: string;
};

export type UserData = {
  id: number;
  name: string;
};

export type FilterData = {
  users: UserData[];
  customers: CustomerData[];
};

export type UserDataFilter = {
  id: number;
  name: string;
};

export type CustomerDataFilter = {
  id: number;
  name: string;
  project?: ProjectData;
};

type SerializedFilters = {
  users: number[];
  customers: { id: number; projectId: number | undefined }[];
};

type Props = {
  data: FilterData;
};

const Wrapper = styled.div`
  margin: 0.5rem;
`;

const ButtonsWrapper = styled.div`
  & > a.button {
    padding: 9px !important; // without important it's overriden by an #id selector from the site's stylesheet
  }
`;

const FilterWrapper = styled.div`
  margin: 0.5rem 0rem;
  margin-top: 0;
  display: flex;
  gap: 0.5rem;

  width: 550px;
  max-width: 100%;
`;

const Column = styled.div`
  max-width: 50%;
  flex: 1 1 0;

  &:first-child {
    margin-left: 0;
  }

  &:last-child {
    margin-right: 0;
  }
`;

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-bottom: 0.25rem;
  margin-top: 0;

  & > * {
    flex: 1 1 0;
  }
`;

const Title = styled.span`
  font-size: 0.9rem;
  font-weight: bold;
`;

const FilterTitle = styled.div`
  font-size: 1.2rem;
  margin: 0.5rem 0;
`;

const PlannerFilters = ({ data }: Props) => {
  const [userFilters, setUserFilters] = useState([] as UserDataFilter[]);
  const [pendingChanges, setPendingChanges] = useState(false);

  const [customerFilters, setCustomerFilters] = useState(
    [] as CustomerDataFilter[]
  );

  const [userModalVisible, setUserModalVisible] = useState(false);
  const [customerModalVisible, setCustomerModalVisible] = useState(false);

  const renderUserFilters = (): React.ReactNode => {
    return (
      <Filters>
        {userFilters.map((user, i) => (
          <FilterEntry
            key={user.id}
            onDelete={() => removeUserFilterByIndex(i)}
          >
            <UserFilterRow user={user} />
          </FilterEntry>
        ))}
      </Filters>
    );
  };

  const renderCustomerFilters = (): React.ReactNode => {
    return (
      <Filters>
        {customerFilters.map((customer, i) => (
          <FilterEntry
            key={`${customer.id}-${customer.project?.id ?? -1}`}
            onDelete={() => removeCustomerFilterByIndex(i)}
          >
            <CustomerFilterRow customer={customer} />
          </FilterEntry>
        ))}
      </Filters>
    );
  };

  function removeUserFilterByIndex(index: number) {
    setUserFilters(userFilters.filter((_, i) => i != index));
    setPendingChanges(true);
  }

  function removeCustomerFilterByIndex(index: number) {
    setCustomerFilters(customerFilters.filter((_, i) => i != index));
    setPendingChanges(true);
  }

  const addUserFilters = (users: UserDataFilter[]) => {
    setUserFilters([...userFilters, ...users]);
    setPendingChanges(true);
  };

  const addCustomerFilters = (customers: CustomerDataFilter[]) => {
    setCustomerFilters([...customerFilters, ...customers]);
    setPendingChanges(true);
  };

  const applyFilters = () => {
    const serializedFilters = {
      users: userFilters.map((uf) => uf.id),
      customers: customerFilters.map((cf) => ({
        id: cf.id,
        projectId: cf.project?.id,
      })),
    } as SerializedFilters;
    const filterJson = JSON.stringify(serializedFilters);

    GlobalScope.filterJson = filterJson;

    const [startDate, endDate] = getSelectedTimeRange();
    setClientStateFilterJson(filterJson);
    updateSessionVariables(startDate);
    refreshDayPilot();

    (window as any).updateScheduledHours(
      startDate.toISOString(),
      endDate.toISOString()
    );

    setPendingChanges(false);
  };

  return (
    <Wrapper>
      <FilterTitle>Filtrera</FilterTitle>
      <FilterWrapper>
        <Column>
          <Title>Personal</Title>
          {renderUserFilters()}
          <ModalButton
            text="Lägg till personal"
            title="Lägg till filter för personal"
            modalVisible={userModalVisible}
            setModalVisible={setUserModalVisible}
            allowContentOverflow={true}
            shouldCloseOnEsc={false}
          >
            <AddUserFilterModal
              users={data.users}
              existingFilters={userFilters}
              onAdd={addUserFilters}
              closeModal={() => setUserModalVisible(false)}
            />
          </ModalButton>
        </Column>
        <Column>
          <Title>Kunder</Title>
          {renderCustomerFilters()}
          <ModalButton
            text="Lägg till kund"
            title="Lägg till filter för kund"
            modalVisible={customerModalVisible}
            setModalVisible={setCustomerModalVisible}
            allowContentOverflow={true}
            shouldCloseOnEsc={false}
          >
            <AddCustomerFilterModal
              customers={data.customers}
              existingFilters={customerFilters}
              onAdd={addCustomerFilters}
              closeModal={() => setCustomerModalVisible(false)}
            />
          </ModalButton>
        </Column>
      </FilterWrapper>
      <ButtonsWrapper>
        <a
          onClick={applyFilters}
          className={classnames(
            "blue",
            "button",
            !pendingChanges && "disabled"
          )}
        >
          Tillämpa filter
        </a>
      </ButtonsWrapper>
    </Wrapper>
  );
};

export default PlannerFilters;
